import {
  type ChangeEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  ContentCard,
  RadioButtons,
  ResponsiveFlex,
  Row,
  Spinner,
} from "@narmi/design_system";
import { modules } from "byzantine";
import {
  useFetchSwiftAdditionalDetails,
  useRecipient,
  useRecipients,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import {
  createDefaultValues,
  useCreateInternationalWireForm,
  useFetchInternationalWireReasons,
  WireTypeEnum,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import { useNotificationContext } from "cerulean";
import HeaderWithSteps from "../../../HeaderWithSteps";
import { useSudoContext } from "../../../SudoContext";
import { RecipientDrawer, DRAWER_TYPES } from "../../../RecipientDrawer";
import * as formAdapters from "../../../../src/adapters";
import styles from "./InternationalWire.module.scss";
import wireContainerStyles from "../WireTransferContainer.module.scss";
import FreeFormUsd from "./FreeFormUsd";
import FreeFormFx from "./FreeFormFx";
import TemplateSelector from "./TemplateSelector";
import { useFormContainer} from "../../../../src/modules/transfers/wire/templates/SendViaTemplate/Form/Form.container";

export const INTERNATIONAL_WIRE_TOTAL_STEPS = 4;

const InternationalWireForm = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const [showRecipientDrawer, setShowRecipientDrawer] =
    useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    getValues,
    setError,
  } = useCreateInternationalWireForm();
  const { options } = useFormContainer(WireTypeEnum.International);
  const methodOptions = Object.fromEntries(options.map((option) => ([option.label, option.id])));
  const isFXForm =
    !!watch("recipientCurrency") &&
    watch("recipientCurrency") !== "USD";
  const [
    showAdditionalDetailsScreen,
    setShowAdditionalDetailsScreen,
  ] = useState(false);
  const additionalDetailsRef =
    useRef<HTMLDivElement>(null);
  const [institutionDetails, setInstitutionDetails] = useState<{
    [key: string]: string;
  } | null>({});
  const selectedRecipientId = watch("recipientId");
  const selectedRecipient = useRecipient(selectedRecipientId);
  const recipients = useRecipients();
  const { establishSudo, onSetInitialSteps } = useSudoContext();
  const { fetchInternationalWireReasons } =
    useFetchInternationalWireReasons();
  const { fetchSwiftAdditionalDetails, loading: loadingBIC } =
    useFetchSwiftAdditionalDetails();
  const [reasonsRes, setReasonsRes] =
    useState<API.InternationalWireReasonResponse>({});
  const isRecipientSwiftMissing =
    selectedRecipient && !selectedRecipient.swift_destination;
  const { sendNotification } = useNotificationContext();
  const {
    internationalWireTemplatesEnabled,
    hasSendIntlWireViaTemplatesPermission,
    hasSendIntlWireFreeformPermission,
    canSendInternationalWire,
    canSendConsumerInternationalWire,
  } = modules.wireTemplates.useWirePermissions();
  const {
    method,
    form: methodForm,
    submitMethod,
  } = modules.wireTemplates.useMethodSelector();
  const {
    onSelectTemplate,
    selectedTemplateId,
    error: templateError,
  } = modules.internationalWires.useSwiftWireTemplateSelector();
  const { templateSelected } =
    modules.internationalWires.useConditionalDetails();
  const { errorMessage: quoteErrorMessage } =
    modules.internationalWires.useTemplateFxQuote();
  const canOnlySendViaTemplate =
    !canSendConsumerInternationalWire &&
    internationalWireTemplatesEnabled &&
    !hasSendIntlWireFreeformPermission &&
    hasSendIntlWireViaTemplatesPermission;
  const showMethodSelector =
    !canSendConsumerInternationalWire &&
    internationalWireTemplatesEnabled &&
    hasSendIntlWireViaTemplatesPermission &&
    !canOnlySendViaTemplate;

  const showFreeform =
    canSendConsumerInternationalWire ||
    (!showMethodSelector && !canOnlySendViaTemplate) ||
    method === "freeform";
  
  const showTemplateSelector =
    !canSendConsumerInternationalWire &&
    ((!showMethodSelector && canOnlySendViaTemplate) || method === "template");

  // Redirect to Step 1 if the form was not started through the normal flow.
  // This handles the case where a full-page navigation (e.g., Manage Templates)
  // destroys form state and the user navigates back.
  useEffect(() => {
    if (!getValues("hasFormBeenStarted") || !canSendInternationalWire) {
      navigate("/transfer/wires", { replace: true });
    }
  }, [getValues, navigate, canSendInternationalWire]);

  const onBack = () => {
    navigate("/transfer/wires");
  };

  const onSubmit = () => {
    establishSudo("/transfer/wires/international/review");
  };

  const handleOpenDrawer = () => {
    setShowRecipientDrawer(true);
  };
  const handleCloseDrawer = (newRecipientId?: API.RecipientId) => {
    setShowRecipientDrawer(false);
    const newRecipient = recipients.find((r) => r.id === newRecipientId);
    if (newRecipientId && newRecipient?.swift_destination) {
      setValue("recipientId", newRecipientId, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    } else if (isRecipientSwiftMissing) {
      setValue("recipientId", "" as API.RecipientId);
      sendNotification({
        type: "negative",
        text: l10n.getString("add-swift-details-error-banner"),
      });
    }
  };

  const onAddRecipientClick = () => {
    setValue("recipientId", "" as API.RecipientId, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    handleOpenDrawer();
  };

  const fetchAndSetAdditionalDetails = async (
    bic: string
  ) => {
    const res = await fetchSwiftAdditionalDetails(bic);
    if (res?.institution_name) {
      setInstitutionDetails({
        institutionName: res.institution_name,
      });
    }
  };

  useEffect(() => {
    if (isRecipientSwiftMissing) {
      handleOpenDrawer();
    }
  }, [selectedRecipient]);

  useEffect(() => {
    onSetInitialSteps(INTERNATIONAL_WIRE_TOTAL_STEPS - 1);
  }, [onSetInitialSteps]);

  useEffect(() => {
    setQuoteError(quoteErrorMessage ?? null);
  }, [quoteErrorMessage]);

  useEffect(() => {
    setQuoteError(null);
  }, [selectedTemplateId]);

  useEffect(() => {
    const dest = selectedRecipient?.swift_destination;
    let reasonsParams:
      | API.FetchInternationalWireReasons.Request
      | undefined;

    if (dest?.bic) {
      reasonsParams = { bic: dest.bic };
      fetchAndSetAdditionalDetails(dest.bic);
    } else if (dest?.correspondent_bank_bic) {
      const lastFour =
        dest.account_identifier?.slice(-4);
      setInstitutionDetails({
        institutionName:
          dest.metadata?.bank_name ?? "",
        ...(lastFour ? { lastFour } : {}),
      });
      const countryCode =
        dest.branch_address?.country_code;
      if (countryCode) {
        reasonsParams = { country: countryCode };
      }
    } else {
      setInstitutionDetails(null);
    }

    if (reasonsParams) {
      fetchInternationalWireReasons(reasonsParams, {
        onSuccess: (response) => {
          setReasonsRes(response);
          const reasons =
            Object.values(response)?.[0];
          const currentReason =
            getValues("wireReason");
          if (
            !reasons?.length ||
            (currentReason &&
              !reasons.some(
                (r) => r.code === currentReason
              ))
          ) {
            setValue("wireReason", "");
            setValue("wireReasonDescription", "");
          }
        },
      });
    } else {
      setReasonsRes({});
      setValue("wireReason", "");
      setValue("wireReasonDescription", "");
    }
  }, [
    selectedRecipient,
    fetchInternationalWireReasons,
    fetchSwiftAdditionalDetails,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    if (
      showAdditionalDetailsScreen &&
      additionalDetailsRef.current
    ) {
      additionalDetailsRef.current.scrollIntoView({
        behavior: "instant",
      });
    }
  }, [showAdditionalDetailsScreen]);

  const renderRecipientInstitutionDetails = () => {
    if (loadingBIC) {
      return (
        <Row className={"margin--top--xs"}>
          <Spinner
            size={22}
            color="var(--theme-secondary)"
          />
        </Row>
      );
    }
    if (institutionDetails?.institutionName) {
      const displayName = institutionDetails.lastFour
        ? `${institutionDetails.institutionName}-${institutionDetails.lastFour}`
        : institutionDetails.institutionName;
      return (
        <Row className={`margin--top--xs ${styles.institutionDetails}`}>
          <Row.Item className={styles.bankNameContainer}>
            <span className={styles.narmiIconBankBox}>
              <span className="narmi-icon-bank" />
            </span>
            <span
              className={`fontColor--secondary ${styles.bankName}`}
              title={displayName}
            >
              {displayName}
            </span>
          </Row.Item>
        </Row>
      );
    }
    return null;
  };

  const renderMethodSelector = () => {
    if (!showMethodSelector || showAdditionalDetailsScreen)
      return null;
    const radioProps = formAdapters.radio(
      methodForm,
      "method"
    );
    return (
      <>
        <ContentCard kind="bordered">
          <p
            className={
              wireContainerStyles.wireContainerSubHeader
            }
          >
            {l10n.getString("heading-wire-method")}
          </p>
          <RadioButtons
            name="method-options"
            options={methodOptions}
            {...radioProps}
            onChange={(
              e: ChangeEvent<HTMLInputElement>
            ) => {
              radioProps.onChange(e);
              const currentCurrency =
                getValues("recipientCurrency");
              reset({
                ...createDefaultValues(),
                recipientCurrency: currentCurrency,
                hasFormBeenStarted: true,
              });
            }}
          />
        </ContentCard>
      </>
    );
  };

  const handleNextClick = () => {
    if (showMethodSelector && !method) {
      submitMethod();
      return;
    }
    if (showTemplateSelector && !selectedTemplateId) {
      setError("swiftWireTemplateId", {
        type: "manual",
        message: l10n.getString(
          "form-generic-validation-required"
        ),
      });
      return;
    }
    handleSubmit(onSubmit, console.error)();
  };

  const showForm = showFreeform || templateSelected;

  return (
    <div ref={additionalDetailsRef}>
      <ResponsiveFlex gapSize="xl">
        <HeaderWithSteps
          headerText={l10n.getString("heading-wire-title")}
        />
        {renderMethodSelector()}
        {showTemplateSelector && (
          <TemplateSelector
            onSelectTemplate={onSelectTemplate}
            selectedTemplateId={selectedTemplateId}
            error={templateError}
          />
        )}
        {isFXForm
          ? showForm && (
              <FreeFormFx
                reasonsRes={reasonsRes}
                onAddRecipientClick={onAddRecipientClick}
                renderRecipientInstitutionDetails={
                  renderRecipientInstitutionDetails
                }
                onBack={onBack}
                onSubmit={onSubmit}
                showAdditionalDetailsScreen={
                  showAdditionalDetailsScreen
                }
                setShowAdditionalDetailsScreen={
                  setShowAdditionalDetailsScreen
                }
              />
            )
          : showForm && (
              <FreeFormUsd
                reasonsRes={reasonsRes}
                onAddRecipientClick={onAddRecipientClick}
                renderRecipientInstitutionDetails={
                  renderRecipientInstitutionDetails
                }
              />
            )}
        {(!isFXForm || !showFreeform) && (
          <>
            {quoteError && (
              <Alert kind="error" isActive isDismissable={false}>
                {quoteError}
              </Alert>
            )}
            <Row alignItems="center">
              <Row.Item>
                <Button
                  label={l10n.getString("button-back")}
                  kind="negative"
                  testId="cancel-international-wire"
                  onClick={onBack}
                  type="button"
                />
              </Row.Item>
              <Row.Item shrink>
                <Button
                  label={l10n.getString("button-next")}
                  testId="continue-international-wire"
                  onClick={handleNextClick}
                />
              </Row.Item>
            </Row>
          </>
        )}
      </ResponsiveFlex>
      <RecipientDrawer
        handleClose={handleCloseDrawer}
        isOpen={showRecipientDrawer}
        fromACHFlow={false}
        fromInternationalWireFlow={true}
        drawerType={
          selectedRecipientId
            ? DRAWER_TYPES.edit
            : DRAWER_TYPES.add
        }
        recipientId={selectedRecipientId}
      />
    </div>
  );
};

export default InternationalWireForm;
